import { extractUserClaims, isCognitoConfigured, isTokenExpired, parseJwt } from "./cognito";
const TOKEN_KEY = "anvesh.hub.token";
const ID_TOKEN_KEY = "anvesh.hub.id_token";
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const API_TIMEOUT_MS = 15_000;

export function getToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  if (isTokenExpired(token)) {
    setToken(null);
    return null;
  }
  return token;
}

export function setToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ID_TOKEN_KEY);
  }
}

async function hub<T = Record<string, unknown>>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = (init.method || "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (!headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);
  const url = API_BASE ? `${API_BASE}${path.startsWith("/") ? path : `/${path}`}` : path;
  const body =
    (method === "POST" || method === "PUT" || method === "PATCH") && init.body === undefined
      ? "{}"
      : init.body;
  const res = await fetch(url, {
    ...init,
    headers,
    body,
    signal: init.signal ?? AbortSignal.timeout(API_TIMEOUT_MS),
  });
  const json = (await res.json().catch(() => ({}))) as T & { message?: string; ok?: boolean };
  if (!res.ok) {
    if (res.status === 401) {
      setToken(null);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("anvesh:unauthorized"));
      }
    }
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export const api = {
  health: () => hub<{
    users: number;
    instances: number;
    message: string;
    systemStats?: {
      memory: { total: number; free: number; usagePercent: number };
      cpu: { load: number; cores: number; usagePercent: number };
    };
  }>("/hub/health"),
  login: (username: string, password: string) =>
    hub<{ token: string; user: HubUser; message: string }>("/hub/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => {
    setToken(null);
    return hub("/hub/auth/logout", { method: "POST" }).catch(() => undefined);
  },
  me: async (): Promise<{ user: HubUser }> => {
    const token = getToken();
    if (!token) {
      throw new Error("No active session");
    }
    const idToken = localStorage.getItem(ID_TOKEN_KEY) || token;
    if (token.split(".").length === 3) {
      if (isTokenExpired(idToken || token)) {
        setToken(null);
        throw new Error("Session expired");
      }
      const claims = extractUserClaims(idToken || token, token);
      return {
        user: {
          id: claims.sub || claims.username,
          username: claims.username,
          role: claims.role,
          createdAt: new Date().toISOString(),
        }
      };
    }
    return hub<{ user: HubUser }>("/hub/auth/me");
  },
  changePassword: (currentPassword: string, newPassword: string) =>
    hub<{ message: string }>("/hub/auth/password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  listUsers: (from = 0, size = 20) =>
    hub<{ users: HubUser[]; total: number; from: number; size: number }>(
      `/hub/users?from=${from}&size=${size}`,
    ),
  createUser: (payload: { username: string; password: string; role: string }) =>
    hub("/hub/users", { method: "POST", body: JSON.stringify(payload) }),
  deleteUser: (id: string) => hub(`/hub/users/${id}`, { method: "DELETE" }),

  listInstances: () => hub<{ instances: HubInstance[]; message?: string }>("/hub/instances"),
  createInstance: (payload: unknown) =>
    hub("/hub/instances", { method: "POST", body: JSON.stringify(payload) }),
  updateInstance: (id: string, payload: unknown) =>
    hub(`/hub/instances/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteInstance: (id: string) => hub(`/hub/instances/${id}`, { method: "DELETE" }),
  healthInstance: (id: string) => hub(`/hub/instances/${id}/health`, { method: "POST" }),

  listIndexes: (engineId: string) =>
    hub<{ indexes: IndexInfo[] }>(`/hub/engines/${engineId}/indexes`),
  getIndex: (engineId: string, name: string) =>
    hub<{ index: IndexDetail }>(`/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}`),
  createIndex: (engineId: string, payload: unknown) =>
    hub<{ ok: boolean; message: string }>(`/hub/engines/${engineId}/indexes`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  
  listSnapshots: (engineId: string, name: string) =>
    hub<{ snapshots: IndexSnapshot[]; message?: string }>(
      `/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}/snapshots`,
    ),
  createSnapshot: (engineId: string, name: string, note?: string) =>
    hub<{ snapshot: IndexSnapshot; message: string }>(
      `/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}/snapshots`,
      { method: "POST", body: JSON.stringify({ note }) },
    ),
  revertSnapshot: (engineId: string, name: string, snapshotId: string) =>
    hub<{ message: string }>(
      `/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}/snapshots/${encodeURIComponent(snapshotId)}/revert`,
      { method: "POST" },
    ),

  deleteIndex: (engineId: string, name: string) =>
    hub(`/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}`, { method: "DELETE" }),
  search: (engineId: string, name: string, payload: unknown) =>
    hub<{ hits: SearchHit[]; total: number; message?: string; tookMs?: number }>(
      `/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}/search`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  validateDocs: (engineId: string, name: string, documents: unknown[]) =>
    hub<{ ok: boolean; issues: { path: string; message: string }[] }>(
      `/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}/validate`,
      { method: "POST", body: JSON.stringify({ documents }) },
    ),
  ingestDocs: (engineId: string, name: string, documents: unknown[], validate = true) =>
    hub(`/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}/ingest`, {
      method: "POST",
      body: JSON.stringify({ documents, validate }),
    }),
  listDocuments: (engineId: string, name: string, from = 0, size = 20) =>
    hub<{ total: number; documents: IndexedDocument[]; message?: string }>(
      `/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}/documents?from=${from}&size=${size}`,
    ),
  deleteDocument: (engineId: string, name: string, id: string) =>
    hub(`/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}/documents/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  clearDocuments: (engineId: string, name: string) =>
    hub<{ deleted: number; message?: string }>(
      `/hub/engines/${engineId}/indexes/${encodeURIComponent(name)}/documents`,
      { method: "DELETE" },
    ),

  listSpiderConfigs: () => hub<{ configs: SpiderConfigRow[] }>("/hub/spider-configs"),
  saveSpiderConfig: (payload: unknown) =>
    hub("/hub/spider-configs", { method: "POST", body: JSON.stringify(payload) }),
  updateSpiderConfig: (id: string, payload: unknown) =>
    hub(`/hub/spider-configs/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteSpiderConfig: (id: string) => hub(`/hub/spider-configs/${id}`, { method: "DELETE" }),
  runSpider: (id: string, payload?: { indexName?: string }) =>
    hub(`/hub/spider-configs/${id}/run`, {
      method: "POST",
      body: payload ? JSON.stringify(payload) : undefined,
    }),

  listIndexerConfigs: () => hub<{ configs: IndexerConfigRow[] }>("/hub/indexer-configs"),
  saveIndexerConfig: (payload: unknown) =>
    hub("/hub/indexer-configs", { method: "POST", body: JSON.stringify(payload) }),
  updateIndexerConfig: (id: string, payload: unknown) =>
    hub(`/hub/indexer-configs/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteIndexerConfig: (id: string) => hub(`/hub/indexer-configs/${id}`, { method: "DELETE" }),
  runIndexer: (id: string) => hub(`/hub/indexer-configs/${id}/run`, { method: "POST" }),

  listJobs: (from = 0, size = 20, status?: string) => {
    const qs = new URLSearchParams({ from: String(from), size: String(size) });
    if (status) qs.set("status", status);
    return hub<{ jobs: HubJob[]; total: number; from: number; size: number }>(
      `/hub/jobs?${qs}`,
    );
  },
  refreshJob: (id: string) => hub(`/hub/jobs/${id}/refresh`, { method: "POST" }),
  cancelJob: (id: string) =>
    hub<{ message: string; job?: HubJob }>(`/hub/jobs/${id}/cancel`, { method: "POST" }),
  deleteJob: (id: string) => hub(`/hub/jobs/${id}`, { method: "DELETE" }),
  clearFinishedJobs: () =>
    hub<{ cleared: number; message: string }>("/hub/jobs/clear-finished", { method: "POST" }),
  fleetHealth: (signal?: AbortSignal) =>
    hub<{
      online: number;
      total: number;
      message: string;
      results: FleetHealthRow[];
    }>("/hub/fleet/health", { method: "POST", signal }),
  engineObservability: (instanceId: string, signal?: AbortSignal) =>
    hub<EngineObservability>(
      `/hub/engines/${encodeURIComponent(instanceId)}/observability`,
      { signal },
    ),
  listAudit: (from = 0, size = 20) =>
    hub<{ entries: AuditEntry[]; total: number; from: number; size: number }>(
      `/hub/audit?from=${from}&size=${size}`,
    ),
  listDeadLetter: (source?: string, index?: string, limit = 50) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (source) qs.set("source", source);
    if (index) qs.set("index", index);
    return hub<{ ok: boolean; total: number; count: number; entries: DeadLetterEntry[] }>(
      `/hub/dead-letter?${qs}`,
    );
  },
  replayDeadLetter: (instanceId: string, indexName: string, ids?: string[]) =>
    hub<{ ok: boolean; replayed: number; failed: number; message: string }>(
      "/hub/dead-letter/replay",
      {
        method: "POST",
        body: JSON.stringify({ instanceId, indexName, ids }),
      },
    ),

  // Spider Schedules
  listSpiderSchedules: () =>
    hub<{ ok: boolean; configured?: boolean; count: number; schedules: SpiderScheduleRow[] }>("/hub/spider/schedules"),
  createSpiderSchedule: (payload: unknown) =>
    hub<{ ok: boolean; message: string; schedule: SpiderScheduleRow }>("/hub/spider/schedules", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getSpiderSchedule: (id: string) =>
    hub<{ ok: boolean; schedule: SpiderScheduleRow; recentRuns?: SpiderScheduleRunRow[] }>(
      `/hub/spider/schedules/${encodeURIComponent(id)}`,
    ),
  updateSpiderSchedule: (id: string, payload: unknown) =>
    hub<{ ok: boolean; message: string; schedule: SpiderScheduleRow }>(
      `/hub/spider/schedules/${encodeURIComponent(id)}`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),
  deleteSpiderSchedule: (id: string) =>
    hub<{ ok: boolean; message: string }>(`/hub/spider/schedules/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  runSpiderScheduleNow: (id: string) =>
    hub<{ ok: boolean; message: string; jobId: string; runId: string }>(
      `/hub/spider/schedules/${encodeURIComponent(id)}/run`,
      { method: "POST" },
    ),
  listSpiderScheduleRuns: (id: string) =>
    hub<{ ok: boolean; count: number; runs: SpiderScheduleRunRow[] }>(
      `/hub/spider/schedules/${encodeURIComponent(id)}/runs`,
    ),

  // Scanner (Multi-source folder/file/db change scanner)
  getScannerStatus: () =>
    hub<{ ok: boolean; configured: boolean; reachable?: boolean; message?: string }>("/hub/scanner/status"),
  listScannerScans: () =>
    hub<{ ok: boolean; configured?: boolean; count: number; scans: ScannerScanRow[] }>("/hub/scanner/scans"),
  triggerScannerScan: (payload: unknown) =>
    hub<{ ok: boolean; jobId: string; message: string }>("/hub/scanner/scans", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getScannerScan: (id: string) =>
    hub<ScannerScanRow>(`/hub/scanner/scans/${encodeURIComponent(id)}`),
};

export interface HubUser {
  id: string;
  username: string;
  role: "admin" | "operator" | "viewer";
  createdAt: string;
}

export type HubInstanceKind =
  | "engine"
  | "indexer"
  | "spider"
  | "scanner"
  | "elasticsearch"
  | "opensearch"
  | "solr";

export interface SpiderScheduleRow {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  cron?: string;
  interval?: string;
  runAt?: string;
  nextRunAt?: string;
  lastRunAt?: string;
  lastJobId?: string;
  lastStatus?: string;
  runCount?: number;
  consecutiveFailures?: number;
  config: Record<string, unknown>;
  autoIndex?: {
    enabled?: boolean;
    index?: string;
    engineUrl?: string;
    apiKey?: string;
    indexerUrl?: string;
    indexerApiKey?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface SpiderScheduleRunRow {
  id: string;
  scheduleId: string;
  jobId: string;
  startedAt: string;
  finishedAt?: string;
  status: string;
  pagesCrawled?: number;
  pagesIndexed?: number;
  message?: string;
  error?: string;
}

export interface ScannerScanRow {
  id: string;
  type: "folder" | "file" | "database";
  status: "queued" | "running" | "completed" | "failed";
  message: string;
  logs: string[];
  totalScanned?: number;
  totalCreated?: number;
  totalModified?: number;
  totalDeleted?: number;
  totalIndexed?: number;
  totalFailed?: number;
  error?: string;
  payload: {
    type: "folder" | "file" | "database";
    target?: {
      index: string;
      engineUrl?: string;
      indexerUrl?: string;
    };
    folderConfig?: {
      path: string;
      recursive?: boolean;
      watch?: boolean;
      includeExtensions?: string[];
      excludePatterns?: string[];
      extractOcr?: boolean;
      extractPalettes?: boolean;
    };
    fileConfig?: {
      path: string;
      format?: string;
      chunkSize?: number;
    };
    dbConfig?: {
      driver?: string;
      connectionString: string;
      table?: string;
      query?: string;
      watermarkColumn?: string;
      primaryKey?: string;
      batchSize?: number;
    };
  };
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export const HUB_SEARCH_INSTANCE_KINDS: HubInstanceKind[] = [
  "engine",
  "elasticsearch",
  "opensearch",
  "solr",
];

export interface HubInstance {
  id: string;
  name: string;
  kind: HubInstanceKind;
  baseUrl: string;
  enabled: boolean;
  hasApiKey?: boolean;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface IndexInfo {
  name: string;
  docCount?: number;
  mappings?: Record<string, { type: string }>;
  settings?: Record<string, unknown>;
}

export interface IndexDetail {
  name: string;
  mappings: Record<string, { type: string }>;
  settings?: Record<string, unknown>;
  docCount?: number;
}

export interface IndexedDocument {
  id: string;
  fields: Record<string, unknown>;
  meta?: Record<string, unknown>;
  updatedAt?: string;
}


export interface IndexSnapshot {
  id: string;
  indexName: string;
  docCount: number;
  checksum: string;
  createdAt: string;
  note?: string;
  sizeBytes?: number;
}

export interface SearchHit {
  id: string;
  score: number;
  source: {
    fields: Record<string, unknown>;
    meta?: Record<string, unknown>;
  };
  highlight?: Record<string, string[]>;
  distanceKm?: number;
}

export interface SpiderConfigRow {
  id: string;
  name: string;
  description?: string;
  instanceId?: string;
  indexName?: string;
  autoIndex?: boolean;
  indexerInstanceId?: string;
  engineInstanceId?: string;
  config: Record<string, unknown>;
  updatedAt: string;
}

export interface IndexerConfigRow {
  id: string;
  name: string;
  description?: string;
  instanceId?: string;
  engineInstanceId?: string;
  indexName: string;
  inputPath?: string;
  batchSize?: number;
  updatedAt: string;
}

export interface HubJob {
  id: string;
  kind: "spider" | "indexer";
  status: string;
  message: string;
  configId?: string;
  configName?: string;
  instanceId?: string;
  remoteJobId?: string;
  output?: string;
  logs?: string[];
  indexName?: string;
  pagesIndexed?: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface FleetHealthRow {
  id: string;
  name: string;
  kind: HubInstanceKind;
  baseUrl?: string;
  enabled: boolean;
  ok: boolean;
  status: number;
  latencyMs: number;
  message: string;
  systemStats?: {
    memory: { total: number; free: number; usagePercent: number };
    cpu: { load: number; cores: number; usagePercent: number };
  };
}

export interface EngineObservability {
  health: {
    storage?: string;
    indexes?: number;
    documents?: number;
    deadLetter?: { totalRecorded?: number };
    resourceGuard?: {
      status?: string;
      heapRatio?: number;
      cpuPercent?: number;
      pacingDelayMs?: number;
    };
    processMemory?: {
      rssBytes: number;
      heapUsedBytes: number;
      heapTotalBytes: number;
      externalBytes: number;
    };
  };
  analytics: {
    totalQueries: number;
    avgLatencyMs: number;
    p95LatencyMs: number;
    zeroResultQueries: number;
    totalClicks: number;
    bufferedEvents?: number;
    droppedEvents?: number;
  };
  telemetry?: {
    pipeline?: {
      maxBufferedEvents: number;
      bufferedEvents: number;
      droppedEvents: number;
      losslessExport: boolean;
      exportConfigured: boolean;
      exportQueueFiles: number;
    };
    observability?: {
      capacity: number;
      buffered: number;
      recorded: number;
      spilled: number;
      dropped: number;
    };
    recentEvents?: Array<{
      at: string;
      service: string;
      kind: string;
      message: string;
      severity: string;
    }>;
    resourceGuard?: {
      status?: string;
      heapRatio?: number;
      cpuPercent?: number;
      pacingDelayMs?: number;
    };
    circuits?: Record<string, unknown>;
  } | null;
}

export interface AuditEntry {
  id: string;
  at: string;
  actorName?: string;
  action: string;
  target?: string;
  detail?: string;
  ok: boolean;
}

export interface DeadLetterEntry {
  id: string;
  recordId?: string;
  source: "engine" | "spider" | "indexer" | "hub";
  targetIndex?: string;
  error: string;
  timestamp: string;
  retryCount: number;
  payload: unknown;
}
